import {
  faBold,
  faChevronUp,
  faItalic,
  faSlashForward,
  faUnderline
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type AnyExtension, mergeAttributes } from '@tiptap/core'
import Emoji from '@tiptap/extension-emoji'
import { Link } from '@tiptap/extension-link'
import {
  Table,
  TableCell,
  TableHeader,
  TableRow
} from '@tiptap/extension-table'
import { TaskItem } from '@tiptap/extension-task-item'
import { TaskList } from '@tiptap/extension-task-list'
import { TextAlign } from '@tiptap/extension-text-align'
import { Placeholder } from '@tiptap/extensions'
import { NodeSelection } from '@tiptap/pm/state'
import {
  type Editor,
  EditorContent,
  useEditor,
  useEditorState
} from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import clsx from 'clsx'
import type React from 'react'
import { type ReactElement, useEffect, useState } from 'react'
import emojiSuggestion from '~/components/Form/Editor/Emoji/suggestion'
import { ImageUpload } from '~/components/Form/Editor/ImageUpload/ImageUploadExtension'
import { addPlaceholderExtension } from '~/components/Form/Editor/Placeholder/PlaceholderExtension'
import { AttachmentAddInput } from '~/components/Form/Input/AttachmentAddInput'
import { AttachmentsInput } from '~/components/Form/Input/AttachmentsInput'
import { ImageAddButton } from '~/components/Form/Input/ImageAddButton'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useConfig } from '~/domains/configuration/queries/config'
import { rawColors } from '~/styles/theme.css'
import { isHtmlEmpty } from '~/utils'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from '../TextInput.css'
import { AlignmentDropdown } from './AlignmentDropdown'
import {
  editorBody,
  editorBottom,
  editorBubbleMenu,
  editorBubbleMenuSeparator,
  editorButtonToggle,
  editorButtonToggleActive,
  editorCompactTables,
  editorContainer,
  editorDisabled,
  editorEmpty,
  editorMenuBottom,
  editorOptions,
  editorOptionsContent,
  editorOptionsContentActive,
  editorOptionsTrigger,
  editorReduced
} from './EditorInput.css'
import { HeadingDropdown } from './HeadingDropdown'
import { ImageAltButton } from './ImageAltButton'
import { LinkButton } from './LinkButton'
import { ListDropdown } from './ListDropdown'
import { TableBubbleMenu } from './TableBubbleMenu'
import { TableButton } from './TableButton'

const createExtensions = (
  legacyUrl: string | null | undefined,
  placeholder: string,
  withEmoticons: boolean,
  placeholderCategories?: string[],
  placeholderItemsFilter?: string[],
  compactTables = false
) =>
  [
    StarterKit.configure({ link: false }),
    Link.configure({ openOnClick: false, autolink: true }),
    TextAlign.configure({
      types: ['heading', 'paragraph', 'tableCell', 'tableHeader']
    }),
    Table.configure({ resizable: true }).extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          bordered: {
            default: true,
            parseHTML: (el) => el.getAttribute('data-bordered') !== 'false',
            renderHTML: ({ bordered }) =>
              bordered === false ? { 'data-bordered': 'false' } : {}
          }
        }
      },
      renderHTML({ HTMLAttributes }) {
        return [
          'table',
          mergeAttributes(HTMLAttributes, {
            style: compactTables
              ? 'border-collapse: collapse; width: auto; table-layout: fixed;'
              : 'border-collapse: collapse; width: 100%; table-layout: fixed;'
          }),
          ['tbody', 0]
        ]
      }
    }),
    TableRow,
    TableHeader.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          bordered: {
            default: true,
            parseHTML: (el) => {
              const style = el.getAttribute('style') ?? ''
              return style.includes('border:') || style.includes('border: ')
            },
            renderHTML: ({ bordered }) => ({
              style:
                bordered !== false
                  ? `border: 1px solid ${rawColors.inactive}; padding: 6px 10px`
                  : 'border: none; padding: 0'
            })
          },
          backgroundColor: {
            default: null,
            parseHTML: (el) => el.style.backgroundColor || null,
            renderHTML: ({ backgroundColor }) =>
              backgroundColor
                ? { style: `background-color: ${backgroundColor}` }
                : {}
          },
          verticalAlign: {
            default: 'top',
            parseHTML: (el) => el.style.verticalAlign || 'top',
            renderHTML: ({ verticalAlign }) => ({
              style: `vertical-align: ${verticalAlign || 'top'}`
            })
          }
        }
      }
    }),
    TableCell.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          bordered: {
            default: true,
            parseHTML: (el) => {
              const style = el.getAttribute('style') ?? ''
              return style.includes('border:') || style.includes('border: ')
            },
            renderHTML: ({ bordered }) => ({
              style:
                bordered !== false
                  ? `border: 1px solid ${rawColors.inactive}; padding: 6px 10px`
                  : 'border: none; padding: 0'
            })
          },
          backgroundColor: {
            default: null,
            parseHTML: (el) => el.style.backgroundColor || null,
            renderHTML: ({ backgroundColor }) =>
              backgroundColor
                ? { style: `background-color: ${backgroundColor}` }
                : {}
          },
          verticalAlign: {
            default: 'top',
            parseHTML: (el) => el.style.verticalAlign || 'top',
            renderHTML: ({ verticalAlign }) => ({
              style: `vertical-align: ${verticalAlign || 'top'}`
            })
          }
        }
      }
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    ImageUpload.configure({
      allowBase64: true,
      legacyUrl: legacyUrl ?? null,
      resize: {
        enabled: true,
        minWidth: 8,
        minHeight: 8,
        alwaysPreserveAspectRatio: true
      }
    }),
    Placeholder.configure({ placeholder, emptyEditorClass: editorEmpty }),
    withEmoticons &&
      Emoji.configure({ enableEmoticons: true, suggestion: emojiSuggestion }),
    addPlaceholderExtension(placeholderCategories, placeholderItemsFilter)
  ].filter(Boolean) as AnyExtension[]

type EditorInputProps = Omit<React.HTMLProps<HTMLDivElement>, 'onChange'> &
  InputInFormProps & {
    label?: string
    contextInfo?: string
    hasAttachments?: boolean
    placeholderCategories?: string[]
    placeholderItemsFilter?: string[]
    options?: ReactElement
    prepend?: ReactElement
    append?: ReactElement
    onChange?: (value: string) => void
    onUploading?: (uploading: boolean) => void
    showBottomMenu?: boolean
    isReduced?: boolean
    content?: string
    isLoadingContent?: boolean
    placeholder?: string
    autofocus?: boolean
    disabled?: boolean
    textOnly?: boolean
    compactToolbar?: boolean
    compactTables?: boolean
  }

const TextModifiers = ({ editor }: { editor: Editor }) => {
  const { isBold, isItalic, isUnderline } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline')
    })
  })
  return (
    <>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={clsx(
          editorButtonToggle,
          isItalic && editorButtonToggleActive
        )}
      >
        <FontAwesomeIcon icon={faItalic} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={clsx(
          editorButtonToggle,
          isUnderline && editorButtonToggleActive
        )}
      >
        <FontAwesomeIcon icon={faUnderline} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={clsx(editorButtonToggle, isBold && editorButtonToggleActive)}
      >
        <FontAwesomeIcon icon={faBold} />
      </button>
      <HeadingDropdown editor={editor} />
      <LinkButton editor={editor} />
    </>
  )
}

const toggleTableBorders = (editor: Editor): void => {
  const { state } = editor.view
  const $pos = state.selection.$from
  let tablePos: number | null = null
  let tableNode: ReturnType<typeof $pos.node> | null = null
  for (let depth = $pos.depth; depth >= 0; depth--) {
    if ($pos.node(depth).type.name === 'table') {
      tablePos = $pos.before(depth)
      tableNode = $pos.node(depth)
      break
    }
  }
  if (!tableNode || tablePos === null) return
  const newBordered = tableNode.attrs.bordered === false
  const tr = state.tr
  tr.setNodeMarkup(tablePos, undefined, {
    ...tableNode.attrs,
    bordered: newBordered
  })
  state.doc.nodesBetween(
    tablePos + 1,
    tablePos + tableNode.nodeSize - 1,
    (node, pos) => {
      if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          bordered: newBordered
        })
      }
    }
  )
  editor.view.dispatch(tr)
}

const BubbleMenuContent = ({ editor }: { editor: Editor }) => {
  const { inTable, hasSelection, isTableBordered, isImage } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const $pos = editor.state.selection.$from
      let isTableBordered = true
      for (let depth = $pos.depth; depth >= 0; depth--) {
        if ($pos.node(depth).type.name === 'table') {
          isTableBordered = $pos.node(depth).attrs.bordered !== false
          break
        }
      }
      return {
        inTable: editor.isActive('table'),
        hasSelection: !editor.state.selection.empty,
        isTableBordered,
        isImage:
          editor.state.selection instanceof NodeSelection &&
          editor.state.selection.node.type.name === 'imageUpload'
      }
    }
  })
  return (
    <>
      {hasSelection && !isImage && <TextModifiers editor={editor} />}
      {isImage && <ImageAltButton editor={editor} />}
      {hasSelection && inTable && <div className={editorBubbleMenuSeparator} />}
      {inTable && (
        <TableBubbleMenu
          editor={editor}
          isTableBordered={isTableBordered}
          onToggleBorders={() => toggleTableBorders(editor)}
        />
      )}
    </>
  )
}

const BaseModifiers = ({
  editor,
  compact
}: {
  editor: Editor
  compact?: boolean
}) => (
  <>
    <ListDropdown editor={editor} />
    {!compact && <TextModifiers editor={editor} />}
    <AlignmentDropdown editor={editor} />
    <TableButton editor={editor} />
  </>
)

export const EditorInput = ({
  control,
  register,
  label,
  name,
  error,
  setValue,
  hasAttachments = false,
  onChange,
  onUploading,
  options,
  prepend,
  append,
  watch,
  placeholderCategories,
  placeholderItemsFilter,
  showBottomMenu = true,
  isReduced = false,
  isLoadingContent = false,
  content = '',
  autofocus = true,
  placeholder = 'Saisir votre message...',
  disabled = false,
  textOnly = false,
  compactToolbar = false,
  compactTables = false
}: EditorInputProps): ReactElement => {
  const [showOptions, setShowOptions] = useState(false)
  const [uploadingCount, setUploadingCount] = useState(0)
  const { data: legacyUrl } = useConfig('organization.legacy_url')
  if (watch !== undefined && name !== undefined) {
    content = watch(name)
  }

  const editor = useEditor({
    extensions: createExtensions(
      legacyUrl ?? null,
      placeholder,
      !textOnly,
      placeholderCategories,
      placeholderItemsFilter,
      compactTables
    ),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: clsx(editorBody, compactTables && editorCompactTables)
      }
    },
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      if (name) {
        setValue?.(name, html, {
          shouldDirty: true,
          shouldValidate: true
        })
      }
      onChange?.(isHtmlEmpty(html) ? '' : html)
    }
  })

  if (autofocus) {
    editor.chain().focus()
  }

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled, false)
  }, [editor, disabled])

  useEffect(() => {
    if (!editor) return

    const checkUploadingImages = () => {
      let count = 0
      editor.state.doc.descendants((node) => {
        if (
          node.type.name === 'imageUpload' &&
          (node.attrs.uploading === true || node.attrs.uploadError !== null)
        ) {
          count++
        }
      })
      setUploadingCount(count)
    }

    editor.on('update', checkUploadingImages)
    checkUploadingImages()

    return () => {
      editor.off('update', checkUploadingImages)
    }
  }, [editor])

  useEffect(() => {
    if (uploadingCount > 0) {
      onUploading?.(true)
    } else {
      onUploading?.(false)
    }
  }, [uploadingCount, onUploading])

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [content, editor])

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        !event.target.closest(`.${editorOptions}`)
      ) {
        setShowOptions(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [])

  if (register !== undefined && name !== undefined) {
    register(name)
  }

  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name ?? ''} className={inputLabel}>
          {label}
        </label>
      )}
      <div
        className={clsx(
          editorContainer,
          error && inputError,
          (disabled || isLoadingContent) && editorDisabled
        )}
      >
        {prepend}
        <EditorContent
          editor={editor}
          content={content ?? null}
          className={clsx(isReduced ? editorReduced : null)}
        />
        {append}
        {!textOnly && (
          <BubbleMenu
            editor={editor}
            className={editorBubbleMenu}
            shouldShow={({ editor }) =>
              !editor.state.selection.empty || editor.isActive('table')
            }
          >
            {editor && <BubbleMenuContent editor={editor} />}
          </BubbleMenu>
        )}
        {showBottomMenu && (
          <div className={editorBottom}>
            {editor && (
              <div className={editorMenuBottom}>
                {hasAttachments && (
                  <AttachmentAddInput
                    className={editorButtonToggle}
                    control={control ?? undefined}
                  />
                )}
                {!textOnly && (
                  <ImageAddButton
                    editor={editor}
                    className={editorButtonToggle}
                  />
                )}
                {!textOnly && (
                  <BaseModifiers editor={editor} compact={compactToolbar} />
                )}
                {placeholderCategories && placeholderCategories.length > 0 && (
                  <Tooltip direction="top" text="Publipostage" asChild>
                    <button
                      type="button"
                      className={editorButtonToggle}
                      onClick={() => editor.commands.insertContent(' /')}
                    >
                      <FontAwesomeIcon icon={faSlashForward} />
                    </button>
                  </Tooltip>
                )}
                {options && (
                  <div className={editorOptions}>
                    <div
                      className={clsx(
                        editorOptionsContent,
                        showOptions && editorOptionsContentActive
                      )}
                    >
                      {options}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowOptions(!showOptions)}
                      className={editorOptionsTrigger}
                    >
                      <FontAwesomeIcon icon={faChevronUp} />
                      Options
                    </button>
                  </div>
                )}
              </div>
            )}
            {hasAttachments && control && watch && (
              <AttachmentsInput control={control} watch={watch} />
            )}
          </div>
        )}
      </div>
      {error && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
